import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { apiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { ShipToCustomerDialog } from "@/components/ShipToCustomerDialog";
import { fmtIDR, ITEM_STATUS } from "@/lib/format";
import { Truck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Allocation() {
  const { eventId } = useOutletContext();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const canBuy = ["shopper", "admin"].some((r) => user.roles.includes(r));
  const canShip = ["shopper", "sales", "admin"].some((r) => user.roles.includes(r));
  const [shipOrder, setShipOrder] = useState(null);
  const delShipment = async (oid, sid) => {
    if (!window.confirm("Hapus catatan pengiriman ini?")) return;
    try { await api.delete(`/orders/${oid}/shipments/${sid}`); toast.success("Pengiriman dihapus"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const load = async () => {
    const [o, c] = await Promise.all([api.get(`/orders?event_id=${eventId}`), api.get("/customers")]);
    setOrders(o.data.filter((x) => x.payment_status === "valid"));
    setCustomers(c.data);
  };
  useEffect(() => { load(); }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (oid, idx, item_status) => {
    try { await api.patch(`/orders/${oid}/items/${idx}/status?item_status=${item_status}`); toast.success("Status diperbarui"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const cname = (id) => customers.find((c) => c.id === id)?.name || "-";

  return (
    <div>
      <PageHeader title="Alokasi & Kirim ke Customer" subtitle="Qty dibeli dialokasikan otomatis dari Bought ke order lunas paling awal. Kirim ke customer bisa bertahap mengikuti barang yang sudah tiba; ongkir ditagihkan ke customer." />
      {shipOrder && <ShipToCustomerDialog order={shipOrder} customerName={cname(shipOrder.customer_id)} onClose={() => setShipOrder(null)} onSaved={() => { setShipOrder(null); load(); }} />}
      <div className="space-y-3">
        {orders.length === 0 && <div className="text-center py-16 text-slate-400 bg-white border rounded-lg">Belum ada order tervalidasi.</div>}
        {orders.map((o) => (
          <div key={o.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`alloc-order-${o.id.slice(0,8)}`}>
            <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b">
              <div><span className="font-mono text-xs text-slate-500">{o.id.slice(0, 8)}</span> <span className="font-semibold text-slate-900 ml-2">{cname(o.customer_id)}</span></div>
              <div className="flex items-center gap-2">
                {o.shipping_fee_total > 0 && <span className="text-xs text-slate-500">Ongkir ditagih: <span className="font-mono font-semibold">{fmtIDR(o.shipping_fee_total)}</span></span>}
                <span className="text-sm font-semibold">{fmtIDR(o.total)}</span>
                {canShip && o.items.some((i) => (i.bought_qty || 0) > (i.shipped_qty || 0)) && <Button size="sm" variant="outline" className="h-8 gap-1" data-testid={`ship-btn-${o.id.slice(0,8)}`} onClick={() => setShipOrder(o)}><Truck className="w-3.5 h-3.5" /> Kirim ke Customer</Button>}
              </div>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-xs uppercase text-slate-500 border-b"><th className="text-left px-4 py-2">Item</th><th>Dipesan</th><th>Dibeli</th><th>Sisa</th><th>Dikirim</th><th>Status</th></tr></thead>
              <tbody>
                {o.items.map((i, idx) => {
                  const remaining = i.qty - i.bought_qty;
                  return (
                    <tr key={`${i.catalog_item_id || i.name}-${idx}`} className="border-b border-slate-100">
                      <td className="px-4 py-2.5">{i.name}<div className="text-xs text-slate-400 font-mono">{i.sku}</div></td>
                      <td className="text-center font-mono font-semibold">{i.qty}</td>
                      <td className="text-center font-mono text-teal-700 font-semibold">{i.bought_qty}</td>
                      <td className="text-center font-mono font-bold" style={{ color: remaining > 0 ? "#e11d48" : "#059669" }}>{remaining}</td>
                      <td className="text-center font-mono text-blue-700 font-semibold" data-testid={`shipped-${o.id.slice(0,8)}-${idx}`}>{i.shipped_qty || 0}</td>
                      <td className="text-center">
                        {canBuy ? (
                          <Select value={i.status} onValueChange={(v) => setStatus(o.id, idx, v)}>
                            <SelectTrigger className="h-8 w-40 text-xs mx-auto" data-testid={`item-status-${o.id.slice(0,8)}-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{Object.keys(ITEM_STATUS).map((s) => <SelectItem key={s} value={s}>{ITEM_STATUS[s].label}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : <StatusBadge map={ITEM_STATUS} value={i.status} />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {o.shipments?.length > 0 && (
              <div className="px-4 py-2 bg-blue-50/40 border-t text-xs space-y-1" data-testid={`shipments-${o.id.slice(0,8)}`}>
                {o.shipments.map((s) => (
                  <div key={s.id} className="flex items-center gap-2">
                    <Truck className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                    <span className="text-slate-500">{new Date(s.shipped_at).toLocaleDateString("id-ID")}</span>
                    <span className="flex-1 truncate">{s.items.map((i) => `${i.name} ×${i.qty}`).join(", ")}{s.courier && ` · ${s.courier}`}{s.resi && ` · ${s.resi}`}</span>
                    <span className="font-mono">{fmtIDR(s.shipping_fee)}</span>
                    {canShip && <button data-testid={`del-shipment-${s.id.slice(0,8)}`} onClick={() => delShipment(o.id, s.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
